import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { miniQuizApi } from '../services/miniQuizService';
import { authApi } from '../services/api';
import { 
  MiniQuizStartResponse, 
  MiniQuizQuestion,
  MiniQuizSession,
  MiniQuizSubmitResponse,
  MiniQuizRemainingAttempts,
  ApiResponse
} from '../types';

interface MiniQuizContextType {
  // State
  session: MiniQuizSession | null;
  questions: MiniQuizQuestion[];
  currentQuestionIndex: number;
  remainingAttempts: number;
  isLoading: boolean;
  isAnswering: boolean;
  error: string | null;

  // Actions
  startQuiz: () => Promise<boolean>;
  submitAnswer: (questionId: string, answer: boolean) => Promise<MiniQuizSubmitResponse | null>;
  getRemainingAttempts: () => Promise<void>;
  resetQuiz: () => void;
  clearError: () => void;
}

const MiniQuizContext = createContext<MiniQuizContextType | undefined>(undefined);

interface MiniQuizProviderProps {
  children: ReactNode;
}

export const MiniQuizProvider: React.FC<MiniQuizProviderProps> = ({ children }) => {
  const [session, setSession] = useState<MiniQuizSession | null>(null);
  const [questions, setQuestions] = useState<MiniQuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnswering, setIsAnswering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Remove the automatic useEffect call to getRemainingAttempts
  // This was causing authentication errors when the app loads

  // Define getRemainingAttempts first so it can be used in startQuiz
  const getRemainingAttempts = useCallback(async (): Promise<void> => {
    // Check if user is authenticated before making the API call
    if (!authApi.isAuthenticated()) {
      console.warn('User not authenticated, skipping getRemainingAttempts call');
      return;
    }

    try {
      const response: ApiResponse<MiniQuizRemainingAttempts> = await miniQuizApi.getRemainingAttempts();
      
      if (response.success && response.data) {
        setRemainingAttempts(response.data.remainingAttempts);
      }
    } catch (err: any) {
      console.error('Error getting remaining attempts:', err);
      // Don't set error for this as it's not critical
    }
  }, []); // Empty dependency array means this function is only created once

  const startQuiz = async (): Promise<boolean> => {
    // Get remaining attempts before starting quiz to ensure we have the latest data
    await getRemainingAttempts();
    
    if (remainingAttempts <= 0) {
      setError('Bạn đã hết lượt chơi trong ngày!');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response: ApiResponse<MiniQuizStartResponse> = await miniQuizApi.startQuiz();
      
      if (response.success && response.data) {
        const { session: newSession, questions: newQuestions } = response.data;

        setSession(newSession);
        setQuestions(newQuestions);
        setCurrentQuestionIndex(newSession?.currentQuestionIndex || 0);
        setIsLoading(false);
        return true;
      } else {
        setError(response.error || 'Lỗi khi bắt đầu quiz');
        setIsLoading(false);
        return false;
      }
    } catch (err: any) {
      console.error('Error starting quiz:', err);
      setError(err.response?.data?.error || 'Lỗi khi bắt đầu quiz');
      setIsLoading(false);
      return false;
    }
  };

  const submitAnswer = async (
    questionId: string, 
    answer: boolean
  ): Promise<MiniQuizSubmitResponse | null> => {
    if (!session || isAnswering) return null;

    setIsAnswering(true);
    setError(null);

    try {
      const response: ApiResponse<MiniQuizSubmitResponse> = await miniQuizApi.submitAnswer(
        session.id,
        questionId,
        answer
      );

      if (response.success && response.data) {
        const result = response.data;
        
        // Update session state
        setSession(prev => prev ? {
          ...prev,
          score: result.score,
          currentQuestionIndex: result.currentQuestionIndex,
          isCompleted: result.isCompleted,
          energyRewarded: result.energyRewarded
        } : null);

        setCurrentQuestionIndex(result.currentQuestionIndex);
        
        // Update remaining attempts if quiz is completed
        if (result.isCompleted) {
          setRemainingAttempts(prev => Math.max(0, prev - 1));
        }

        setIsAnswering(false);
        return result;
      } else {
        setError(response.error || 'Lỗi khi gửi câu trả lời');
        setIsAnswering(false);
        return null;
      }
    } catch (err: any) {
      console.error('Error submitting answer:', err);
      setError(err.response?.data?.error || 'Lỗi khi gửi câu trả lời');
      setIsAnswering(false);
      return null;
    }
  };

  const resetQuiz = (): void => {
    setSession(null);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setError(null);
    setIsAnswering(false);
  };

  const clearError = (): void => {
    setError(null);
  };

  const value: MiniQuizContextType = {
    session,
    questions,
    currentQuestionIndex,
    remainingAttempts,
    isLoading,
    isAnswering,
    error,
    startQuiz,
    submitAnswer,
    getRemainingAttempts,
    resetQuiz,
    clearError
  };

  return (
    <MiniQuizContext.Provider value={value}>
      {children}
    </MiniQuizContext.Provider>
  );
};

export const useMiniQuiz = (): MiniQuizContextType => {
  const context = useContext(MiniQuizContext);
  if (context === undefined) {
    throw new Error('useMiniQuiz must be used within a MiniQuizProvider');
  }
  return context;
};
